'use client';

/**
 * Multi-surface partner reassignment dialog for the IAM Orgs tab.
 *
 * Calls `partners.changePartner` (dry-run preview → apply) so the same
 * audit trail and surface fan-out as the CLI / REST endpoint fire here.
 * Platform-admin only — gated in the parent sheet via `isSuperAdmin`.
 */
import * as React from 'react';
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  Handshake,
  Loader2,
  MinusCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import type { inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from '@tagadapay/trpc-types';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { IamErrorAlert } from '@/lib/iam/IamErrorAlert';
import {
  toIamFriendlyError,
  type IamFriendlyError,
} from '@/lib/iam/friendly-errors';
import { trpc } from '@/lib/api/trpc';

const PARTNER_UNASSIGN = '__unassign__';
const REASON_MIN = 5;

type OrgRef = {
  orgClerkId: string;
  orgClerkName: string | null;
  partnerSlug: string | null;
  partnerName: string | null;
};

type ChangePartnerResult =
  inferRouterOutputs<AppRouter>['partners']['changePartner'];

type SurfacePayload = {
  status?: string;
  rows?: number;
  updated?: number;
  patched?: number;
  error?: string;
  fromWhitelabel?: string | null;
  toWhitelabel?: string | null;
};

const SURFACE_LABELS: Record<string, string> = {
  postgresAccounts: 'Postgres accounts',
  postgresHubRequests: 'Hub requests',
  postgresTagadapayAccounts: 'TagadaPay accounts (TPAs)',
  clerkOrg: 'Clerk org metadata',
  clerkUsers: 'Clerk user tags',
  stripeSubaccounts: 'Stripe pricing groups',
  clickHouseBackfill: 'ClickHouse history',
  hubAssignmentsTags: 'Hub assignment tags',
};

function partnerLabel(
  slug: string | null,
  partners: ReadonlyArray<{ slug: string; name: string }>,
): string {
  if (!slug) return 'None (unaffiliated)';
  const hit = partners.find((p) => p.slug === slug);
  return hit?.name ?? slug;
}

function statusBadgeVariant(
  status: string | undefined,
  dryRun: boolean,
): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (status === 'error') return 'destructive';
  if (status === 'partial') return 'destructive';
  if (status === 'noop') return 'secondary';
  if (dryRun && status === 'ok') return 'outline';
  return 'default';
}

function statusLabel(status: string | undefined, dryRun: boolean): string {
  if (!status) return '—';
  if (dryRun) {
    if (status === 'ok') return 'Will update';
    if (status === 'noop') return 'Already correct';
    if (status === 'deferred') return 'Deferred';
  }
  if (status === 'ok') return 'Updated';
  if (status === 'noop') return 'No change';
  if (status === 'partial') return 'Partial';
  if (status === 'deferred') return 'Deferred';
  if (status === 'error') return 'Failed';
  return status;
}

function surfaceDetail(key: string, payload: SurfacePayload | undefined): string | null {
  if (!payload) return null;
  if (typeof payload.rows === 'number' && payload.rows > 0) {
    return `${payload.rows} row${payload.rows === 1 ? '' : 's'}`;
  }
  if (typeof payload.updated === 'number') {
    return `${payload.updated} user${payload.updated === 1 ? '' : 's'}`;
  }
  if (typeof payload.patched === 'number' && payload.patched > 0) {
    return `${payload.patched} subaccount${payload.patched === 1 ? '' : 's'}`;
  }
  if (key === 'clerkOrg') {
    return `${payload.fromWhitelabel ?? '∅'} → ${payload.toWhitelabel ?? '∅'}`;
  }
  if (payload.error) return payload.error;
  return null;
}

export function ChangePartnerDialog({
  org,
  open,
  onOpenChange,
  partners,
  onApplied,
}: {
  org: OrgRef;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  partners: ReadonlyArray<{ slug: string; name: string }>;
  onApplied: (next: { partnerSlug: string | null; partnerName: string | null }) => void;
}) {
  const utils = trpc.useUtils();
  const [toPartnerRaw, setToPartnerRaw] = React.useState<string>(PARTNER_UNASSIGN);
  const [reason, setReason] = React.useState('');
  const [preview, setPreview] = React.useState<ChangePartnerResult | null>(null);
  const [previewKey, setPreviewKey] = React.useState('');
  const [lastError, setLastError] = React.useState<IamFriendlyError | null>(null);

  const toPartner =
    toPartnerRaw === PARTNER_UNASSIGN ? null : toPartnerRaw;

  const currentKey = `${toPartnerRaw}::${reason.trim()}`;

  React.useEffect(() => {
    if (!open) {
      setToPartnerRaw(org.partnerSlug ?? PARTNER_UNASSIGN);
      setReason('');
      setPreview(null);
      setPreviewKey('');
      setLastError(null);
      return;
    }
    setToPartnerRaw(org.partnerSlug ?? PARTNER_UNASSIGN);
    setReason('');
    setPreview(null);
    setPreviewKey('');
    setLastError(null);
  }, [open, org.orgClerkId, org.partnerSlug]);

  React.useEffect(() => {
    if (previewKey && previewKey !== currentKey) {
      setPreview(null);
      setPreviewKey('');
    }
  }, [currentKey, previewKey]);

  const changeMutation = trpc.partners.changePartner.useMutation({
    onMutate: () => setLastError(null),
    onError: (e) => setLastError(toIamFriendlyError(e)),
  });

  const isUnchanged =
    (org.partnerSlug ?? null) === toPartner;

  const validateForm = (): string | null => {
    if (isUnchanged) {
      return 'Select a different partner or choose unassign if the org is currently affiliated.';
    }
    const trimmed = reason.trim();
    if (trimmed.length < REASON_MIN) {
      return `Reason must be at least ${REASON_MIN} characters (required for the audit log).`;
    }
    return null;
  };

  const runChange = async (dryRun: boolean) => {
    const validationError = validateForm();
    if (validationError) {
      setLastError({
        title: 'Cannot proceed',
        description: validationError,
        suggestion: null,
        variant: 'warning',
      });
      return;
    }

    const result = await changeMutation.mutateAsync({
      scope: { kind: 'org', clerkOrgId: org.orgClerkId },
      toPartner,
      reason: reason.trim(),
      dryRun,
    });

    if (dryRun) {
      setPreview(result);
      setPreviewKey(currentKey);
      return;
    }

    toast.success('Partner assignment updated');
    await utils.iamOrgs.list.invalidate();
    const nextName =
      toPartner === null
        ? null
        : (partners.find((p) => p.slug === toPartner)?.name ?? toPartner);
    onApplied({ partnerSlug: toPartner, partnerName: nextName });
    onOpenChange(false);
  };

  const previewStale = !!preview && previewKey !== currentKey;
  const canApply =
    !!preview &&
    !previewStale &&
    preview.dryRun &&
    !changeMutation.isPending &&
    !isUnchanged;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[min(90vh,720px)] flex-col gap-0 overflow-hidden p-0 sm:max-w-lg">
        <DialogHeader className="space-y-2 border-b px-6 pb-4 pt-6">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Handshake className="h-4 w-4 text-muted-foreground" />
            Change partner
          </DialogTitle>
          <DialogDescription className="text-xs leading-relaxed">
            Reassign{' '}
            <span className="font-medium text-foreground">
              {org.orgClerkName ?? org.orgClerkId}
            </span>
            . Updates Clerk org metadata, Postgres, Stripe pricing, ClickHouse,
            and related surfaces. Recorded in the partner change audit log.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
          <div className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2.5 text-xs">
            <span className="text-muted-foreground">Current</span>
            <Badge variant="secondary" className="font-normal">
              {partnerLabel(org.partnerSlug, partners)}
            </Badge>
            <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span className="text-muted-foreground">New</span>
            <Badge
              variant={toPartner ? 'default' : 'outline'}
              className="font-normal"
            >
              {partnerLabel(toPartner, partners)}
            </Badge>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="change-partner-target" className="text-xs font-medium">
              New partner
            </Label>
            <Select
              value={toPartnerRaw}
              onValueChange={setToPartnerRaw}
              disabled={changeMutation.isPending}
            >
              <SelectTrigger id="change-partner-target" className="h-9">
                <SelectValue placeholder="Select partner…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={PARTNER_UNASSIGN}>
                  None (unassign / direct Tagada)
                </SelectItem>
                {partners.map((p) => (
                  <SelectItem key={p.slug} value={p.slug}>
                    {p.name} ({p.slug})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="change-partner-reason" className="text-xs font-medium">
              Reason
              <span className="ml-1 font-normal text-muted-foreground">
                (audit log, min {REASON_MIN} chars)
              </span>
            </Label>
            <Textarea
              id="change-partner-reason"
              placeholder="e.g. Merchant moved to this partner per contract signed May 2026"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={changeMutation.isPending}
              className="min-h-[4.5rem] resize-none text-sm"
            />
          </div>

          {preview && !previewStale && (
            <div className="space-y-2 rounded-md border bg-card p-3">
              <div className="flex items-center justify-between gap-2">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Preview
                </h4>
                <span className="font-mono text-[10px] text-muted-foreground">
                  {preview.logId}
                </span>
              </div>
              {!preview.ok && (
                <Alert variant="destructive" className="py-2">
                  <AlertCircle className="h-3.5 w-3.5" />
                  <AlertTitle className="text-xs">
                    Some surfaces may fail
                  </AlertTitle>
                  <AlertDescription className="text-xs">
                    Review the list below. Partial failures can be retried — the
                    orchestrator is idempotent per surface.
                  </AlertDescription>
                </Alert>
              )}
              <ul className="space-y-1.5">
                {Object.entries(
                  (preview.surfaces ?? {}) as Record<string, SurfacePayload>,
                ).map(([key, payload]) => {
                  const detail = surfaceDetail(key, payload);
                  return (
                    <li
                      key={key}
                      className="flex items-start justify-between gap-2 text-xs"
                    >
                      <span className="text-muted-foreground">
                        {SURFACE_LABELS[key] ?? key}
                      </span>
                      <div className="flex flex-col items-end gap-0.5">
                        <Badge
                          variant={statusBadgeVariant(payload?.status, true)}
                          className="px-1.5 py-0 text-[10px] font-medium"
                        >
                          {statusLabel(payload?.status, true)}
                        </Badge>
                        {detail ? (
                          <span className="max-w-[200px] truncate text-[10px] text-muted-foreground">
                            {detail}
                          </span>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
              {preview.accountIds.length > 0 && (
                <p className="text-[10px] text-muted-foreground">
                  {preview.accountIds.length} account
                  {preview.accountIds.length === 1 ? '' : 's'} in scope
                </p>
              )}
            </div>
          )}

          {previewStale && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertTitle className="text-xs">Preview outdated</AlertTitle>
              <AlertDescription className="text-xs">
                Partner or reason changed since the last preview. Run preview
                again before applying.
              </AlertDescription>
            </Alert>
          )}

          {lastError && (
            <IamErrorAlert
              error={lastError}
              onDismiss={() => setLastError(null)}
            />
          )}
        </div>

        <DialogFooter className="gap-2 border-t bg-muted/20 px-6 py-4 sm:justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={changeMutation.isPending}
          >
            Cancel
          </Button>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={changeMutation.isPending || isUnchanged}
              onClick={() => void runChange(true)}
            >
              {changeMutation.isPending && changeMutation.variables?.dryRun ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
              )}
              Preview
            </Button>
            <Button
              size="sm"
              disabled={!canApply}
              onClick={() => void runChange(false)}
            >
              {changeMutation.isPending && !changeMutation.variables?.dryRun ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <MinusCircle className="mr-1.5 h-3.5 w-3.5" />
              )}
              Apply change
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
